import Link from "next/link";
import { useMemo, useRef, useState, useEffect } from "react";

import { ChevronRight } from "@mui/icons-material";

import { Menu } from "@components/menu";
import { useMediaQueryBreakpoint } from "@hooks";

import { AnimatedBackground } from "./AnimatedBackground";
import { useTypingAnimation } from "./animation";
import {
  AnimatedBackgroundMask,
  BracketStyled,
  ButtonStyled,
  EverybodyTextStyled,
  HeroTextContainer,
  ITBuddyTextStyled,
  SrOnlyStyled,
  StyledContainer,
  ToScaleTextStyled,
} from "./HeroCard.styled";
import { animatedBackgroundGridSize, generateOpacityMatrix } from "./utils";

export interface HeroCardProps {
  title: string;
  subtitle: string;
  animationPrefix: string;
  animationWords: string[];
  ctaLinkHref: string;
  ctaButtonText: string;
  ctaButtonTextShort: string;
}

export const HeroCard = ({
  title,
  subtitle,
  animationPrefix,
  animationWords,
  ctaLinkHref,
  ctaButtonText,
  ctaButtonTextShort,
}: HeroCardProps) => {
  const width = 1240;
  const height = 550;
  const columns = width / animatedBackgroundGridSize;
  const rows = height / animatedBackgroundGridSize;
  const ref = useRef<SVGSVGElement>(null);

  const opacityMatrix = useMemo(
    () => generateOpacityMatrix(columns, rows),
    [columns, rows],
  );

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Detect client-side hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleMouseMove = ({
    clientX,
    clientY,
  }: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;

    const { x, y } = ref.current.getBoundingClientRect();

    setMousePosition({
      x: clientX - x,
      y: clientY - y,
    });
  };

  // Use the animation hook only after hydration
  const animatedText = useTypingAnimation(animationWords);

  const { isDesktopXl, isTablet } = useMediaQueryBreakpoint();

  // SEO-friendly list of all animation words
  const renderSeoWordsList = () => (
    <SrOnlyStyled as="ul">
      {animationWords.slice(1).map((word, index) => (
        <li key={index}>
          {animationPrefix}
          {word}{" "}
        </li>
      ))}
    </SrOnlyStyled>
  );

  return (
    <StyledContainer
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsMouseDown(true)}
      onMouseLeave={() => setIsMouseDown(false)}
    >
      {isTablet && <Menu fixed />}
      {isTablet && (
        <AnimatedBackgroundMask>
          <AnimatedBackground
            columns={columns}
            rows={rows}
            opacityMatrix={opacityMatrix}
            className="absolute bottom-[-8px]"
            width={width}
            height={height}
          />
          <AnimatedBackground
            ref={ref}
            columns={columns}
            rows={rows}
            opacityMatrix={opacityMatrix}
            gradient
            className="absolute bottom-[-8px]"
            width={width}
            height={height}
            style={{
              opacity: isMouseDown ? "1" : "0",
              maskComposite: "subtract",
              maskImage: `radial-gradient(100px at ${mousePosition.x}px ${mousePosition.y}px, black 30%, transparent 100%)`,
              transition: "opacity 0.3s",
            }}
          />
        </AnimatedBackgroundMask>
      )}

      <HeroTextContainer>
        <EverybodyTextStyled>{title}</EverybodyTextStyled>
        <ITBuddyTextStyled>{subtitle}</ITBuddyTextStyled>

        {/* Display all animation words for SEO when server-side rendering */}
        {!isClient && (
          <>
            <ToScaleTextStyled aria-hidden="true">
              {animationPrefix + animationWords[0]}
            </ToScaleTextStyled>
            {renderSeoWordsList()}
          </>
        )}

        {/* Use animation after client-side hydration */}
        {isClient && (
          <ToScaleTextStyled>
            {animationPrefix + animatedText}
          </ToScaleTextStyled>
        )}
      </HeroTextContainer>

      <Link href={ctaLinkHref} passHref>
        <ButtonStyled
          variant={"contained"}
          endIcon={<ChevronRight />}
          disableElevation
        >
          {isDesktopXl ? ctaButtonText : ctaButtonTextShort}
        </ButtonStyled>
      </Link>

      <BracketStyled />
    </StyledContainer>
  );
};
